import { useEffect, useRef } from "react";
import { useLang } from "../i18n";
import "./Story.css";
import { initStoryPage } from "./storyRuntime";

const PATH_LINKS = ["/services", "/services", "/services", "/services"];

export default function Story() {
  const rootRef = useRef(null);
  const { lang, t } = useLang();
  const isJp = lang === "jp";

  useEffect(() => {
    if (!rootRef.current) return undefined;
    return initStoryPage(rootRef.current);
  }, []);

  return (
    <div className="story-page" data-lang={lang} ref={rootRef}>
      <section className="c1" id="crossing" aria-label="The crossing">
        <div className="c1-stage">
          <canvas className="c1-stars" id="c1-stars" aria-hidden="true"></canvas>
          <div className="c1-wave" id="c1-wave" aria-hidden="true"></div>
          <canvas className="c1-birds" id="c1-birds" aria-hidden="true"></canvas>
          <div className="c1-mount" id="globe-hero-mount"></div>
          <div className="hero-lockup" aria-label="Ruten Borealis, Kakehashi, People Plans Possibilities, Japan to Canada">
            <div className="hero-mark" aria-hidden="true">
              <img className="hero-mark-dark" src="/images/logo-mark-dark.png" alt="" />
              <img className="hero-mark-light" src="/images/logo-mark-light.png" alt="" />
            </div>
            <h1>Ruten Borealis</h1>
            <p className="hero-jp"><span lang="ja">架け橋</span><span className="romaji">Kakehashi</span></p>
            <p className="hero-tagline">{t("story.tagline")}</p>
            <p className="hero-corridor">Japan <span className="arrow" aria-hidden="true">↔</span> Canada</p>
          </div>
          <div className="c1-foot">
            <div className="c1-clock" id="clk-ca">
              <i className="ind"></i>
              <span className="city">{isJp ? "" : t("story.toronto")}</span>
              <span className="jp" lang={isJp ? "ja" : undefined}>{isJp ? t("story.toronto") : ""}</span>
              <span className="t">--:--</span><span className="s">—</span>
            </div>
            <div className="c1-cue" aria-hidden="true"><span>{t("story.scrollCue")}</span><span></span></div>
            <div className="c1-clock" id="clk-jp">
              <i className="ind"></i>
              <span className="city">{isJp ? "" : t("story.tokyo")}</span>
              <span className="jp" lang={isJp ? "ja" : undefined}>{isJp ? t("story.tokyo") : ""}</span>
              <span className="t">--:--</span><span className="s">—</span>
            </div>
          </div>
        </div>
        <aside className="scrub" id="scrub" aria-label="Time of day preview">
          <div className="scrub-row">
            <div className="scrub-time"><span id="sc-time">--:--</span><span id="sc-mood">—</span></div>
            <button id="sc-live" aria-pressed="true">Live</button>
            <button id="sc-sweep" aria-pressed="false">Sweep</button>
          </div>
          <input type="range" id="sc-range" min="0" max="24" step="0.05" defaultValue="3" aria-label="Viewer local time" />
          <div className="scrub-ticks"><span>00</span><span>06</span><span>12</span><span>18</span><span>24</span></div>
          <div className="scrub-row" id="sc-presets">
            <button data-h="3">3 am</button><button data-h="6.2">Dawn</button><button data-h="12.5">Midday</button><button data-h="18.9">Dusk</button>
          </div>
          <div className="scrub-layers">
            <span className="lbl">Version</span>
            <div className="scrub-row" id="sc-variant">
              <button data-v="A">A · Fixed</button><button data-v="B">B · Single ink</button><button data-v="C">C · No grid</button>
            </div>
          </div>
          <div className="scrub-layers">
            <span className="lbl">Layers</span>
            <div className="scrub-row" id="sc-layers">
              <button data-l="coast">Continents</button><button data-l="aurora">Aurora</button><button data-l="birds">Birds</button><button data-l="dragon">Dragon</button><button data-l="momiji">Momiji</button><button data-l="trace">Arc trace</button><button data-l="wave">Seigaiha</button>
            </div>
          </div>
          <label className="scrub-zone">Viewer zone <select id="sc-zone"></select></label>
          <div className="scrub-hint">Preview tool only · press H to hide</div>
        </aside>
      </section>

      <section className="c2" id="paths" aria-labelledby="c2-title">
        <div className="c2-thread" data-thread></div>
        <div className="c2-in">
          <header data-reveal>
            <p className="c2-label">{t("story.ch2Label")}</p>
            <h2 id="c2-title">{t("story.ch2Title")}</h2>
            <p className="c2-lede">{t("story.ch2Lede")}</p>
          </header>
          <ul className="c2-grid">
            {PATH_LINKS.map((href, index) => (
              <li data-reveal style={{"--i":index}} key={index}>
                <a className="c2-card" href={href}>
                  <span className="c2-num">0{index + 1}</span>
                  <h3>{t(`story.paths.${index}.title`)}</h3>
                  <p>{t(`story.paths.${index}.body`)}</p>
                  <span className="c2-go"><span>{t(`story.paths.${index}.cta`)}</span><span aria-hidden="true">→</span></span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="c3" id="process" aria-labelledby="c3-title">
        <div className="c3-in">
          <div className="c3-side">
            <header className="c3-head">
              <p className="c3-label"><b>03</b>{t("story.ch3Label")}</p>
              <h2 id="c3-title">{t("story.ch3Title")}</h2>
              <p className="c3-lede">{t("story.ch3Lede")}</p>
            </header>
            <nav className="c3-map" aria-label="The four stages at a glance">
              <p className="c3-map-cap">{t("story.routeCaption")}</p>
              <ol>
                <li className="c3-map-track" aria-hidden="true"><span className="c3-map-fill"></span></li>
                {[0,1,2,3].map((index) => (
                  <li key={index}><button type="button" data-go={index}><i></i><b>{t(`story.stages.${index}.name`)}</b><span></span></button></li>
                ))}
              </ol>
            </nav>
          </div>
          <ol className="c3-steps">
            <li className="c3-track" aria-hidden="true"><span className="c3-fill"></span></li>
            {[0,1,2,3].map((index) => (
              <li className="c3-step" key={index}>
                <span className="c3-dot" aria-hidden="true"></span>
                <div className="c3-body">
                  <span className="c3-num">Stage 0{index + 1}</span>
                  <h3>{t(`story.stages.${index}.name`)}</h3>
                  <p>{t(`story.stages.${index}.body`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="c4" id="founders" aria-labelledby="c4-title">
        <header className="c4-head" data-scrub>
          <p className="c4-label">{t("story.ch4Label")}</p>
          <h2 id="c4-title">{t("story.ch4Title")}</h2>
          <p className="c4-lede">{t("story.ch4Lede")}</p>
        </header>
        <div className="c4-grid">
          <figure className="c4-portrait c4-kp" data-scrub>
            <figcaption>{t("story.portraitKazuki")}</figcaption>
          </figure>
          <div className="c4-profile c4-kt" data-scrub>
            <span className="c4-role">{t("story.coFounder")}</span>
            <h3>{t("story.kazuki")}</h3>
            <ul className="c4-tags">
              {[0,1,2].map((index) => <li key={index}>{t(`story.kazukiTags.${index}`)}</li>)}
            </ul>
            <p>{t("story.kazukiBody")}</p>
          </div>
          <div className="c4-br" data-bridge aria-hidden="true">
            <span className="c4-l c4-l1"></span><span className="c4-l c4-l2"></span>
            <span className="c4-circle"><b>{t("story.bridgeWord")}</b><small></small></span>
          </div>
          <figure className="c4-portrait c4-hp" data-scrub>
            <figcaption>{t("story.portraitKaho")}</figcaption>
          </figure>
          <div className="c4-profile c4-ht" data-scrub>
            <span className="c4-role">{t("story.coFounder")}</span>
            <h3>{t("story.kaho")}</h3>
            <ul className="c4-tags">
              {[0,1].map((index) => <li key={index}>{t(`story.kahoTags.${index}`)}</li>)}
            </ul>
            <p>{t("story.kahoBody")}</p>
          </div>
        </div>
      </section>

      <section className="c5" id="start" aria-labelledby="c5-title">
        <div className="c5-panel">
          <div data-reveal>
            <p className="c5-label">{t("story.ch5Label")}</p>
            <h2 id="c5-title">{t("story.ch5Title")}</h2>
            <p className="c5-body">{t("story.ch5Body")}</p>
            <div className="c5-actions">
              <a className="c5-btn" href="/contact">
                <b>{t("story.ch5Button")}</b>
              </a>
              <span className="c5-note">{t("story.ch5Note")}</span>
            </div>
          </div>
        </div>
      </section>

      <button className="rb-top" type="button" aria-hidden="true" tabIndex="-1">{t("story.top")}<span aria-hidden="true">↑</span></button>
      <a className="rb-cta" href="/contact" aria-hidden="true" tabIndex="-1"><b>{t("story.floatingCta")}</b></a>
    </div>
  );
}
